<template>
    <g v-if="navPath && navPathShape" class="layer layer-map-nav-path" :style="navStyle">
        <g v-if="showOffRouteConnector">
            <line
                v-for="(link, index) in offrouteConnectorLinksOnFloor"
                :key="`offroute-link-${index}`"
                class="map-nav-offroute-connector"
                :x1="link.masterItem.x"
                :y1="link.masterItem.y"
                :x2="link.slaveItem.x"
                :y2="link.slaveItem.y" />
        </g>

        <template v-if="isPathDebugVisible || (instruction && instruction.index > 0)">
            <g v-for="(step, index) in orderedPathSteps" :key="'link-' + index">
                <template v-if="isSameFloor(step.link) || isPathDebugVisible">
                    <line class="map-nav-segment" :class="{
                        'active': isActive(step.link),
                        'traversed': isTraversedStep(index),
                        'not-traversed': !isTraversedStep(index),
                        'hidden': !step.link.visible,
                        'different-floor': !isSameFloor(step.link)
                    }" :x1="step.start.x" :y1="step.start.y" :x2="step.end.x" :y2="step.end.y" />

                    <line v-if="userMarkerPosition && currentUserStepIndex === index" class="map-nav-segment user-nav-segment" :x1="step.start.x" :y1="step.start.y" :x2="userMarkerPosition.x" :y2="userMarkerPosition.y" />

                    <text v-if="isPathDebugVisible" :x="step.link.getMiddlePoint().x" :y="step.link.getMiddlePoint().y"
                        font-weight="600" :style="{ fontSize: getScaleCSS(14) }">
                        {{step.link.index}}
<!--                        {{ step.link.angle }}° {{ step.link.distance | round }}m-->
                    </text>
                </template>
            </g>
        </template>
        <template v-else>
            <path :d="navPathShape.path" class="map-nav-path" fill="transparent" />
        </template>
        <path :d="navPathShape.path" class="map-nav-path-dot-animation" fill="transparent" />
    </g>
</template>

<script>
import MapMixin from "@/helpers/maps/map-mixin.js";
export default {
    name: `layer-map-nav-path`,
    mixins: [MapMixin],
    props: {
        linkSelected: { type: Object },
        instruction: { type: Object },
        floor: { type: Object },
        navPath: { type: Object },
        offrouteConnector: { type: Object, default: null },
        userMarkerPosition: { type: Object, default: null },
        userLink: { type: Object, default: null }
    },
    computed: {
        navStyle() {
            return {
                "--nav-path-length": this.navPathShape.distance,
                "--nav-path-duration": `${Math.min(5, this.navPathShape.duration)}s`
            }
        },
        showOffRouteConnector() {
            const connector = this.offrouteConnector;
            if (!connector || !this.floor || !Array.isArray(connector.links) || connector.links.length === 0) {
                return false;
            }
            if (!connector.floorId || !this.floor.id) {
                return false;
            }
            return connector.floorId === this.floor.id && this.offrouteConnectorLinksOnFloor.length > 0;
        },
        offrouteConnectorLinksOnFloor() {
            const connectorLinks = this.offrouteConnector?.links;
            if (!Array.isArray(connectorLinks) || !this.floor?.id) {
                return [];
            }
            return connectorLinks.filter((link) => {
                return !!link
                    && !!link.masterItem
                    && !!link.slaveItem
                    && isFinite(link.masterItem.x)
                    && isFinite(link.masterItem.y)
                    && isFinite(link.slaveItem.x)
                    && isFinite(link.slaveItem.y)
                    && link.floorId === this.floor.id;
            });
        },
        navPathShape() {
            return this.navPath.getFloorPath(this.floor.id);
        },
        orderedPathSteps() {
            const steps = [];
            const pathItems = this.navPath?.items || [];
            const pathLinks = this.navPath?.links || [];
            if (!Array.isArray(pathItems) || pathItems.length < 2 || !Array.isArray(pathLinks) || pathLinks.length === 0) {
                return steps;
            }

            const usedLinkIndexes = new Set();
            for (let i = 0; i < pathItems.length - 1; i++) {
                const start = pathItems[i];
                const end = pathItems[i + 1];
                if (!start || !end) continue;

                let foundLink = null;
                let foundLinkIndex = -1;

                for (let linkIndex = 0; linkIndex < pathLinks.length; linkIndex++) {
                    if (usedLinkIndexes.has(linkIndex)) continue;
                    const candidate = pathLinks[linkIndex];
                    if (!candidate?.masterItem || !candidate?.slaveItem) continue;
                    const direct = this.isSameMapItem(candidate.masterItem, start) && this.isSameMapItem(candidate.slaveItem, end);
                    const reverse = this.isSameMapItem(candidate.masterItem, end) && this.isSameMapItem(candidate.slaveItem, start);
                    if (!direct && !reverse) continue;
                    foundLink = candidate;
                    foundLinkIndex = linkIndex;
                    break;
                }

                if (!foundLink) {
                    for (let linkIndex = 0; linkIndex < pathLinks.length; linkIndex++) {
                        const candidate = pathLinks[linkIndex];
                        if (!candidate?.masterItem || !candidate?.slaveItem) continue;
                        const direct = this.isSameMapItem(candidate.masterItem, start) && this.isSameMapItem(candidate.slaveItem, end);
                        const reverse = this.isSameMapItem(candidate.masterItem, end) && this.isSameMapItem(candidate.slaveItem, start);
                        if (!direct && !reverse) continue;
                        foundLink = candidate;
                        foundLinkIndex = linkIndex;
                        break;
                    }
                }

                if (foundLink) {
                    usedLinkIndexes.add(foundLinkIndex);
                    steps.push({ link: foundLink, start, end });
                }
            }

            return steps;
        },
        currentPathItemIndex() {
            if (!this.instruction?.currentItem || !Array.isArray(this.navPath?.items)) {
                return -1;
            }
            return this.navPath.items.findIndex((item) => {
                return item === this.instruction.currentItem
                    || (item?.id && this.instruction.currentItem?.id && item.id === this.instruction.currentItem.id);
            });
        },
        currentUserStepIndex() {
            if (!this.userLink || !Array.isArray(this.orderedPathSteps) || this.orderedPathSteps.length === 0) {
                return -1;
            }

            const marker = this.userMarkerPosition;
            const hasMarker = !!marker && isFinite(marker.x) && isFinite(marker.y);
            let bestMatch = null;

            for (let i = 0; i < this.orderedPathSteps.length; i++) {
                const step = this.orderedPathSteps[i];
                const navLink = step?.link;
                const matches = navLink === this.userLink
                    || (navLink?.id && this.userLink?.id && navLink.id === this.userLink.id);
                if (!matches) continue;

                if (!hasMarker) {
                    return i;
                }

                const ratio = this.projectPointOnSegmentRatio(marker.x, marker.y, step.start.x, step.start.y, step.end.x, step.end.y);
                const projectedX = step.start.x + ((step.end.x - step.start.x) * ratio);
                const projectedY = step.start.y + ((step.end.y - step.start.y) * ratio);
                const dx = marker.x - projectedX;
                const dy = marker.y - projectedY;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (!bestMatch || dist < bestMatch.dist) {
                    bestMatch = { index: i, dist };
                }
            }

            return bestMatch ? bestMatch.index : -1;
        },
        traversedStepCount() {
            if (!Array.isArray(this.orderedPathSteps) || this.orderedPathSteps.length === 0) {
                return 0;
            }
            const instructionTraversedCount = this.currentPathItemIndex > 0 ? this.currentPathItemIndex : 0;
            const markerTraversedCount = this.currentUserStepIndex >= 0 ? this.currentUserStepIndex : -1;
            return markerTraversedCount >= 0
                ? Math.min(markerTraversedCount, this.orderedPathSteps.length)
                : Math.min(instructionTraversedCount, this.orderedPathSteps.length);
        }
    },
    methods: {
        isSameMapItem(a, b) {
            if (!a || !b) return false;
            return a === b || (!!a.id && !!b.id && a.id === b.id);
        },
        projectPointOnSegmentRatio(px, py, ax, ay, bx, by) {
            const abx = bx - ax;
            const aby = by - ay;
            const denom = (abx * abx) + (aby * aby);
            if (denom <= 0) return 0;
            const apx = px - ax;
            const apy = py - ay;
            const t = ((apx * abx) + (apy * aby)) / denom;
            return Math.max(0, Math.min(1, t));
        },
        isSameFloor(link) {
            return link.floorId === this.floor.id;
        },
        isTraversedStep(stepIndex) {
            return stepIndex < this.traversedStepCount;
        },
        isActive(link) {
            return this.isPathDebugVisible && this.instruction && this.instruction.links.includes(link);
        }
    }
}
</script>

<style lang="scss" scoped>
.layer-map-nav-path {
    z-index: 0;

    --nav-path-length: 100;
    --nav-path-duration: 10s;

    .map-nav-path-dot-animation {
        stroke: var(--map-primary-color);
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: calc(18px * var(--px-to-svg));
        stroke-dasharray: 0, var(--nav-path-length);
        stroke-dashoffset: var(--nav-path-length);
        animation: dash var(--nav-path-duration) linear infinite;
        animation-delay: 1s;
        pointer-events: none;
    }

    .map-nav-offroute-connector {
        stroke: color-mix(in srgb, var(--map-primary-color) 75%, white);
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: calc(5px * var(--px-to-svg));
        stroke-dasharray: 0, 1;
        opacity: 0.95;
    }
    .map-nav-segment {
        stroke: var(--map-primary-color);
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: calc(8px * var(--px-to-svg));

        &.active {
            stroke: var(--map-secondary-color);
        }

        &.traversed {
            stroke: var(--map-primary-color);
            // stroke: color-mix(in srgb, var(--map-primary-color) 70%, black);
        }

        &.not-traversed {
            stroke: color-mix(in srgb, var(--map-primary-color) 40%, white);
        }

        &.active.traversed {
            stroke: color-mix(in srgb, var(--map-secondary-color) 70%, black);
        }

        &.hidden {
            display: none;
            stroke-width: calc(8 * var(--px-to-svg));
            stroke-dasharray: 0, calc(16px * var(--px-to-svg));
        }

        &.different-floor {
            opacity: 0.5;
            stroke-dasharray: 1, 2;
        }
    }

    .map-nav-path {
        stroke: var(--map-primary-color);
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: calc(8px * var(--px-to-svg));
        stroke-dasharray: var(--nav-path-length);
        stroke-dashoffset: var(--nav-path-length);
        animation: dash var(--nav-path-duration) linear forwards;
        animation-delay: 1s;
        pointer-events: none;

        &.active {
            stroke: var(--map-secondary-color);
        }

        &.different-floor {
            opacity: 0.5;
            stroke-dasharray: 1, 2;
        }
    }

    @keyframes dash {
        to {
            stroke-dashoffset: 0;
        }
    }
}
</style>
